import { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import styles from "./Login.module.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  loginUser,
  resendOtp,
  clearError,
  clearOtpState,
} from "../../../store/slices/authSlice";
import { MdOutlineLanguage } from "react-icons/md";
import { toast } from "react-toastify";
import { googleLogin } from "../../../store/slices/authSlice";

const UNVERIFIED_KEYWORDS = [
  "not verified",
  "unverified",
  "verify",
  "verified",
  "غير مفعل",
  "لم يتم التحقق",
  "تحقق",
  "مفعل",
];

const isUnverifiedError = (err) => {
  if (!err) return false;
  const msg = typeof err === "string" ? err : "";
  return UNVERIFIED_KEYWORDS.some((kw) =>
    msg.toLowerCase().includes(kw.toLowerCase()),
  );
};

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    loading,
    googleLoading,
    error,
    isAuthenticated,
    otpResent,
    otpLoading,
  } = useSelector((s) => s.auth);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [showResendBlock, setShowResendBlock] = useState(false);
  const location = useLocation();
  const handleGoogleLogin = () => {
    dispatch(googleLogin());
  };

  useEffect(() => {
    if (!isAuthenticated) return;

toast.success(t("common.welcome"), {
  autoClose: 1500,
  position: "top-center",
});

navigate(location.state?.from?.pathname || "/dashboard");  }, [isAuthenticated]);

  useEffect(() => {
    if (!otpResent) return;
toast.success(t("common.otpSent"), {
  autoClose: 2000,
  position: "top-center",
});

    dispatch(clearOtpState());

    navigate("/verify-otp", { state: { email: email.trim() } });
  }, [otpResent]);

  useEffect(() => {
    if (!error) return;

    if (isUnverifiedError(error)) {
      setShowResendBlock(true);

      dispatch(clearError());

      return;
    }

    setErrors((p) => ({
      ...p,

      password: typeof error === "string" ? error : "بيانات الدخول غير صحيحة",
    }));

    dispatch(clearError());
  }, [error, dispatch]);

  const validate = () => {
    const newErrors = { email: "", password: "" };

    if (!email.trim()) newErrors.email = "البريد الإلكتروني مطلوب";

    if (!password.trim()) newErrors.password = "كلمة المرور مطلوبة";

    setErrors(newErrors);

    return !newErrors.email && !newErrors.password;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setShowResendBlock(false);

    if (!validate()) return;

    dispatch(loginUser({ email: email.trim(), password }));
  };

  const handleResendOtp = () => {
    if (!email.trim()) {
      setErrors((p) => ({ ...p, email: "أدخل البريد الإلكتروني أولاً" }));

      return;
    }

    dispatch(resendOtp(email.trim()));
  };

  return (
    <div className={styles.login}>
      <div className={styles.loginContainer}>
        <div className={styles.topBar}>
          <img src="/os-logo.png" alt="logo" />

          <div className="desktop-language-dropdown">
            <button
              className={`btn custom-toggle ${styles.langBtn}`}
              data-bs-toggle="dropdown"
            >
              <MdOutlineLanguage />

              {i18n.language === "ar" ? "اللغه" : "Language"}
            </button>

            <ul className="dropdown-menu">
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    i18n.changeLanguage("ar");

                    localStorage.setItem("lang", "ar");
                  }}
                >
                  {t("navbar.language_ar")}
                </button>
              </li>

              <li>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    i18n.changeLanguage("en");

                    localStorage.setItem("lang", "en");
                  }}
                >
                  {t("navbar.language_en")}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.loginCard}>
          <h2>{t("login.title")}</h2>

          <p className={styles.desc}>{t("login.desc")}</p>

          {showResendBlock && (
            <div className={styles.unverifiedBanner}>
              <div className={styles.unverifiedText}>
                <p className={styles.unverifiedTitle}>
                  {t("common.unverifiedTitle")}
                </p>

                <p className={styles.unverifiedSub}>
                  {t("common.unverifiedSub")}
                </p>
              </div>

              <button
                className={styles.resendOtpBtn}
                onClick={handleResendOtp}
                disabled={otpLoading}
                type="button"
              >
                {otpLoading ? t("common.sending") : t("common.resendCode")}{" "}
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.inputGroup}>
              <label htmlFor="email">{t("login.email")}</label>

              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);

                  setShowResendBlock(false);

                  if (errors.email) setErrors((p) => ({ ...p, email: "" }));
                }}
                placeholder="example@email.com"
                className={errors.email ? styles.inputError : ""}
              />

              {errors.email && (
                <span className={styles.fieldError}>{errors.email}</span>
              )}
            </div>

            <div className={`${styles.inputGroup} ${styles.passwordGroup}`}>
              <div className={styles.passwordHeader}>
                <label htmlFor="password">{t("login.password")}</label>

                <button
                  type="button"
                  className={styles.forgotLink}
                  onClick={() => navigate("/forgot-password")}
                >
                  {t("login.forgot")}
                </button>
              </div>

              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);

                  if (errors.password)
                    setErrors((p) => ({ ...p, password: "" }));
                }}
                placeholder={t("login.password")}
                className={errors.password ? styles.inputError : ""}
              />

              <span
                className={styles.togglePassword}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </span>

              {errors.password && (
                <span className={styles.fieldError}>{errors.password}</span>
              )}
            </div>

            <button
              type="submit"
              className={`${styles.button} ${styles.buttonLogin}`}
              disabled={loading}
            >
              {loading ? t("common.loginLoading") : t("login.submit")}{" "}
            </button>
          </form>

          <span>{t("login.or")}</span>

          <button
            className={`${styles.button} ${styles.buttonGoogle}`}
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            type="button"
          >
            {googleLoading ? t("common.googleLoading") : t("login.google")}{" "}
          </button>

          <p className={styles.signup}>
            {t("login.noAccount")}{" "}
            <button
              type="button"
              className={styles.joinNow}
              onClick={() => navigate("/register")}
            >
              {t("login.signup")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
